import React, { useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { Title, useNotify } from "react-admin"
import {
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core"
import getHttpsCallable from "../../utils/getHttpsCallable"


const leadSourceDefault = "BMW-VSV-Import"
const importExcel = getHttpsCallable("admin-bmwVsvImport")

async function importExcelWithCloudFunc (
  base64file: string,
  leadSource: string,
  isDryRun = true,
) {
  try {
    const res: Record<string, any> = await importExcel({
      base64file,
      leadSource,
      isDryRun,
    })

    if (res.data && res.data.error) {
      throw new Error(res.data.error.message)
    }

    return res.data
  }
  catch (err: any) {
    throw new Error(err.message)
  }
}


function uploadAndImport (
  file: Blob,
  leadSource: string,
  isDryRun = true,
): Promise<any> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onabort = () => {
      reject(new Error("File reading was aborted"))
    }

    reader.onerror = (error) => {
      reject(error)
    }

    reader.onload = (e) => {
      let encoded = reader.result?.toString().replace(/^data:(.*,)?/, "")

      if (encoded && (encoded.length % 4) > 0) {
        encoded += "=".repeat(4 - (encoded.length % 4))
      }

      if (encoded) {
        importExcelWithCloudFunc(encoded || "", leadSource, isDryRun)
          .then(result => {
            console.info(result)
            resolve(result)
          })
          .catch(error => {
            console.error(error)
            reject(error)
          })
      }
      else {
        reject(new Error("No valid Excel file was submitted"))
      }
    }

    reader.readAsDataURL(file)
  })
}


function getDataGrid (
  isImporting: boolean,
  setIsImporting: (val: boolean) => any,
  importIsCompleted: boolean,
  setImportIsCompleted: (val: boolean) => any,
  importedRecords: any,
  setImportedRecords: (records: any) => any,
  notify: any,
  file: Blob | undefined,
  leadSource: string,
) {
  if (isImporting || !importedRecords.length || !file) {
    return
  }

  const columnFields = importedRecords && importedRecords.length
    ? Object
      .keys(importedRecords[0])
      .filter(key =>
        ![
          "claim.id",
          "claim.importedAt",
          "claim.userEmail",
          "claim.userId",
          "claim.userRegistrationTime",
          "user.createdAt",
          "user.id",
          "user.importedAt",
          "user.phone",
          "user.registrationTime",
          "user.updatedTime",
        ].includes(key),
      )
    : []

  return (
    <div>
      <div style={{ margin: "4em 0" }}>
        <Typography variant="h6">
          Following records will be imported:
        </Typography>

        <Table size="small">
          <TableHead>
            <TableRow>
              { columnFields.map(name =>
                <TableCell key={name} align="right">{name}</TableCell>)
              }
            </TableRow>
          </TableHead>
          <TableBody>
            { importedRecords.map((rec: Record<string, any>) =>
              <TableRow key={rec.id}>
                { columnFields
                  .map((name: string, index) =>
                    <TableCell key={index} align="right">
                      { String(rec[name]) }
                    </TableCell>,
                  )
                }
              </TableRow>,
            )}
          </TableBody>
        </Table>
      </div>

      { !importIsCompleted &&
          <Button
            variant="contained"
            onClick={() => {
              setIsImporting(true)
              uploadAndImport(file, leadSource, false)
                .then(
                  (result: any) => {
                    setIsImporting(false)
                    setImportIsCompleted(true)
                    setImportedRecords(result.records)
                    notify(result.message, "success")
                  },
                )
                .catch((error: any) => {
                  setIsImporting(false)
                  notify(error.message, "error")
                })
            }}
          >
            Import Records
          </Button>
      }
    </div>
  )
}


function CustomDropZone () {
  const [importedRecords, setImportedRecords] = useState([])
  const [isImporting, setIsImporting] = useState(false)
  const [importIsCompleted, setImportIsCompleted] = useState(false)
  const [fileToImport, setFileToImport] = useState()
  const [leadSource, setLeadSource] = useState(leadSourceDefault)
  const notify = useNotify()
  const onDrop = useCallback(
    acceptedFiles => {
      setImportIsCompleted(false)

      if (acceptedFiles.length !== 1) {
        notify(
          "Only importing of single files is currently supported",
          "warning",
        )
      }
      else {
        setFileToImport(acceptedFiles[0])

        try {
          setIsImporting(true)
          uploadAndImport(acceptedFiles[0], leadSource, true)
            .then(
              (result: any) => {
                setIsImporting(false)
                setImportedRecords(result.records)
                notify(result.message, "success")
              },
            )
            .catch((error: any) => {
              setIsImporting(false)
              notify(error.message, "warning")
            })
        }
        catch (error) {
          // @ts-ignore
          notify(error.message, "warning")
        }
      }
    },
    [leadSource],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "2em",
        }}
      >
        <TextField
          label="Lead Source"
          id="leadSource"
          defaultValue={leadSourceDefault}
          onChange={(event: any) => {
            setLeadSource(event.target.value)
          }}
          style={{ marginBottom: "2em" }}
          variant="outlined"
        />
      </div>
      <div
        {...getRootProps()}
        style={{
          border: "2px dashed gray",
          borderRadius: "10px",
          padding: "4em",
          textAlign: "center",
          cursor: "pointer",
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="subtitle1" component="p">
          {
            isDragActive
              ? "Drop the Excel file here ..."
              : "Drag & drop a BMW VSV Excel file here, " +
                "or click to select an Excel file"
          }
        </Typography>
      </div>

      { isImporting && <p>Loading the Excel File ...</p> }

      {
        getDataGrid(
          isImporting,
          setIsImporting,
          importIsCompleted,
          setImportIsCompleted,
          importedRecords,
          setImportedRecords,
          notify,
          fileToImport,
          leadSource,
        )
      }
    </>
  )
}


export default function BmwVsvImport () {
  return (
    <Card>
      <Title title="BMW VSV Import" />
      <CardContent>
        <p>
          This importer supports importing BMW VSV claims from Excel files.
          Existing claims with the same VSV ID will not be duplicated.
        </p>
        <p>
          The first row will be skipped (headers). Expected columns:
        </p>
        <ul>
          <li>A: <code>vsv_id</code></li>
          <li>B: <code>created_at</code></li>
          <li>C: <code>vehicle_ownership</code> (owner or sold)</li>
          <li>D: <code>current_mileage</code></li>
          <li>E: <code>mileage_on_sales_contract</code></li>
          <li>F: <code>first_name</code></li>
          <li>G: <code>last_name</code></li>
          <li>H: <code>e_mail</code></li>
          <li>I: <code>phone</code></li>
          <li>J: <code>adress</code></li>
          <li>K: <code>postcode</code></li>
          <li>L: <code>city</code></li>
          <li>M: <code>legal_expenses_insurance</code> (true/false)</li>
          <li>N: <code>insurance_provider</code></li>
          <li>O: <code>legal_expenses_insurance_policy_number</code></li>
        </ul>

        <hr style={{ margin: "2em 0" }}/>

        <CustomDropZone />
      </CardContent>
    </Card>
  )
}
